import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { onSessionExpired, refreshSession, setAccessToken } from '../../services/http';
import { writePreference } from '../../utils/storage';
import { authApi } from './api';
import { applyBrand } from './branding';
import type { Me, Scope } from './types';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: Status;
  me: Me | null;
  login(input: { workspace: string; email: string; password: string }): Promise<Me>;
  platformLogin(input: { email: string; password: string; totpCode?: string }): Promise<Me>;
  logout(): Promise<void>;
  reload(): Promise<Me | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Owns the web session: restores it from the refresh cookie on load, exposes login/logout, and
 * applies tenant branding. Permissions here only shape the UI; the API enforces them.
 */
export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const loadMe = useCallback(async (): Promise<Me | null> => {
    try {
      const profile = await authApi.me();
      setMe(profile);
      setStatus('authenticated');
      applyBrand(profile.branding?.primaryColor);
      return profile;
    } catch {
      setAccessToken(null);
      setMe(null);
      setStatus('anonymous');
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      try {
        const ok = await refreshSession();
        if (!active) return;
        if (ok) await loadMe();
        else setStatus('anonymous');
      } catch {
        if (active) setStatus('anonymous');
      }
    };
    void restore();
    const unsubscribe = onSessionExpired(() => {
      setMe(null);
      setStatus('anonymous');
      queryClient.clear();
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [loadMe, queryClient]);

  const login = useCallback(
    async (input: { workspace: string; email: string; password: string }) => {
      const result = await authApi.login(input);
      setAccessToken(result.accessToken);
      writePreference('workspace', input.workspace);
      const profile = await loadMe();
      if (!profile) throw new Error('Signed in, but the profile could not be loaded.');
      return profile;
    },
    [loadMe],
  );

  const platformLogin = useCallback(
    async (input: { email: string; password: string; totpCode?: string }) => {
      const result = await authApi.platformLogin(input);
      setAccessToken(result.accessToken);
      const profile = await loadMe();
      if (!profile) throw new Error('Signed in, but the profile could not be loaded.');
      return profile;
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => undefined);
    setAccessToken(null);
    setMe(null);
    setStatus('anonymous');
    applyBrand(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, login, platformLogin, logout, reload: loadMe }),
    [status, me, login, platformLogin, logout, loadMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

const SCOPE_RANK: Record<Scope, number> = { own: 1, assigned: 2, team: 3, branch: 4, tenant: 5, platform: 6 };

/** UI convenience only: does the signed-in user hold `code` (at `scope` or wider)? */
export function useCan() {
  const { me } = useAuth();
  return useCallback(
    (code: string, scope?: Scope) => {
      const grant = me?.permissions[code];
      if (!grant) return false;
      return scope ? SCOPE_RANK[grant.scope] >= SCOPE_RANK[scope] : true;
    },
    [me],
  );
}
